/**
 * Money is formatted per the active language - the reason the API keeps ICU enabled
 * (InvariantGlobalization=false) and the portal never hard-codes "$".
 *
 * The business runs in the US, so BOTH languages use US numeric conventions:
 * Spanish here is the Spanish of US drivers and dispatchers (es-US → "$1,850.00"),
 * never es-ES, whose "1.850,00 US$" reads foreign to the operation (TRKC-65). The
 * language codes from i18n resolve through this one map so every Intl call in the
 * portal agrees — keyed by base language, so regional variants ("es-ES", "es-MX",
 * "es-419") land on the same pin instead of smuggling their own conventions in.
 */
const INTL_LOCALES: Record<string, string> = {
  en: 'en-US',
  es: 'es-US',
}

function intlLocale(language: string): string {
  return INTL_LOCALES[new Intl.Locale(language).language] ?? language
}

export function formatMoney(amount: number, currency: string, locale: string): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount)
}

/**
 * What a reading of a rate confirmation cost (Stage 3, 3.1): cents and fractions of a cent —
 * a reading on Haiku costs less than one, and "$0.00" would say it cost nothing.
 */
export function formatCost(amount: number, locale: string): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(amount)
}

/**
 * A stated dollar figure — an insurance limit — the way the certificate prints it:
 * "$1,000,000", never "$1,000,000.00". A limit is whole dollars by domain rule (the
 * API refuses cents), so this never rounds a stored value away; it only declines to
 * print the cents money formatting insists on.
 */
export function formatWholeDollars(amount: number, locale: string): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount)
}

export function formatNumber(value: number, locale: string): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    // Counts and measures persist two decimals at most (numeric(8,2)). A fractional
    // value wears both of them — "44,000.50", never a ragged "44,000.5" beside the
    // platform's money — while whole numbers stay bare.
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value)
}

export function formatDate(isoDate: string, locale: string): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    dateStyle: 'medium',
    // A DateOnly is a calendar fact with no zone: formatting in UTC keeps
    // "2026-07-31" from rendering as Jul 30 for anyone west of Greenwich.
    timeZone: 'UTC',
  }).format(new Date(isoDate))
}

/**
 * The business's own time zone — the twin of the API's Business:TimeZone, where the yard is.
 * Every instant the portal shows, or reads a day out of, is read here.
 */
const BUSINESS_TIME_ZONE = 'America/Los_Angeles'

export function formatDateTime(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    dateStyle: 'medium',
    timeStyle: 'short',
    // Shown in the business's own time zone — the twin of the API's
    // Business:TimeZone: an upload stamped 01:10 UTC happened yesterday evening
    // in the yard, and that is the moment the dispatcher remembers.
    timeZone: BUSINESS_TIME_ZONE,
  }).format(new Date(iso))
}

/**
 * The day an instant fell on, in the business's time zone, without the time — for
 * facts where the day is the whole story ("opted out Sep 17"). Not `formatDate`:
 * that one reads a calendar date in UTC, and an instant stamped 01:10 UTC belongs to
 * the evening before in the yard.
 */
export function formatBusinessDate(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    dateStyle: 'medium',
    timeZone: BUSINESS_TIME_ZONE,
  }).format(new Date(iso))
}

/**
 * The calendar day an instant fell on in the business's time zone, as a date box holds a day
 * (2026-10-13): what a filter compares an upload with, so it judges the day the row shows. An
 * upload at 02:00 UTC on the 14th shows 7:00 PM on the 13th, and a filter from the 14th read
 * off its UTC day kept it (GPT-6.1 Sol on #59).
 */
export function businessDayOf(iso: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(iso))
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((candidate) => candidate.type === type)?.value ?? ''
  return `${part('year')}-${part('month')}-${part('day')}`
}

/** Items joined the way the language joins a list: "address and phone", "dirección y teléfono". */
export function formatList(items: readonly string[], locale: string): string {
  return new Intl.ListFormat(intlLocale(locale), { type: 'conjunction' }).format(items)
}

/**
 * Labels joined to sit inside a sentence: lower-cased, so "Air ride" and "Lift gate" read
 * "needs air ride and lift gate" — and, to open one, the first letter raised again.
 */
export function formatPhraseList(labels: readonly string[], locale: string, capitalized = false): string {
  const phrase = formatList(
    labels.map((label) => label.toLocaleLowerCase(intlLocale(locale))),
    locale,
  )
  return capitalized && phrase.length > 0 ? phrase[0]!.toLocaleUpperCase(intlLocale(locale)) + phrase.slice(1) : phrase
}

export function formatPercentage(value: number, locale: string): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'percent',
    // The commission rate persists four decimals (numeric(7,4)); the screen shows
    // exactly what is stored, because a charge computed from 8.1249% must never sit
    // next to a profile reading 8.12%. Ordinary rates (8, 7.5) render unchanged.
    maximumFractionDigits: 4,
  }).format(value / 100)
}

/**
 * Text compared the way a reader of the language sorts it: no case, accents after their
 * letter, and numbers inside a name in number order ("Truck 9" before "Truck 10"). What a
 * table's sorted column compares its words with (TRKC-110).
 */
export function compareTextIn(locale: string): (a: string, b: string) => number {
  return new Intl.Collator(intlLocale(locale), { sensitivity: 'base', numeric: true }).compare
}
